// Whiteboard — small UI parts: the icon set, hover cards and pop-overs.
// Everything floats inside the board's own LAYER (not document.body), so it
// still shows when the board is in full screen.

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const LayerContext = createContext(null);

const P = {
  select: <path d="M5 3l14 8-6 1.6L10 19z" />,
  lasso: <><path d="M7 15.5C4.6 14.5 3 12.6 3 10.5 3 6.9 7 4 12 4s9 2.9 9 6.5S17 17 12 17c-1 0-2-.1-2.9-.3" /><path d="M7 15.5c-.7 1.6-.2 3.3 1.5 4.5" /><circle cx="8.6" cy="16.2" r="1.6" /></>,
  hand: <path d="M8 13V5.5a1.5 1.5 0 013 0V12m0-6.5V4a1.5 1.5 0 013 0v8m0-6a1.5 1.5 0 013 0v7m0-4a1.5 1.5 0 013 0V15a6 6 0 01-6 6h-1.5a6 6 0 01-4.7-2.3L5 15.2a1.6 1.6 0 012.4-2.1L8 14" />,
  // a fingertip tapping: fingers draw too (touchOff: only the pen draws)
  touch: <><path d="M9.5 14V7.5a1.5 1.5 0 013 0V13m0-2a1.5 1.5 0 013 0v2m0-1a1.5 1.5 0 013 0v4a5 5 0 01-5 5h-1.2a5 5 0 01-4.1-2.2L6 16.4a1.5 1.5 0 012.4-1.8l1.1 1.4" /><path d="M7 7.5a4 4 0 018 0" /></>,
  touchOff: <><path d="M9.5 14V7.5a1.5 1.5 0 013 0V13m0-2a1.5 1.5 0 013 0v2m0-1a1.5 1.5 0 013 0v4a5 5 0 01-5 5h-1.2a5 5 0 01-4.1-2.2L6 16.4a1.5 1.5 0 012.4-1.8l1.1 1.4" /><path d="M7 7.5a4 4 0 018 0" /><path d="M3.5 3.5l17 17" /></>,
  pen: <><path d="M14.5 4.5l5 5L9 20H4v-5z" /><path d="M12.5 6.5l5 5" /></>,
  // a chisel-tip marker over the band of colour it leaves
  highlighter: <><path d="M3.5 20.5h9" strokeWidth="3.4" stroke="#facc15" strokeLinecap="butt" /><path d="M14.6 3.6l5.8 5.8-7.6 7.6-5.8-5.8z" /><path d="M7 11.2l-1.6 4.4 3 3 4.4-1.6" /><path d="M5.4 15.6L4 19l1 1 3.4-1.4" /><path d="M16.8 5.8l2.4-2.4" /></>,
  // drawing annotations
  dimension: <><path d="M4 6v12" /><path d="M20 6v12" /><path d="M4 14h16" /><path d="M7 12l-3 2 3 2" /><path d="M17 12l3 2-3 2" /><path d="M9.5 9.5h5" strokeWidth="1.4" /></>,
  fcf: <><rect x="2.5" y="7" width="19" height="10" rx=".5" /><path d="M9 7v10" /><path d="M16 7v10" /><circle cx="5.75" cy="12" r="1.8" /><path d="M3.6 12h4.3M5.75 9.9v4.2" strokeWidth="1.2" /><path d="M11 12h3" strokeWidth="1.3" /><path d="M18 10.5v3" strokeWidth="1.3" /></>,
  datum: <><rect x="7" y="2.5" width="10" height="9" rx=".5" /><path d="M10 9.5l2-5 2 5M10.8 7.8h2.4" strokeWidth="1.3" /><path d="M12 11.5v6" /><path d="M8.5 21.5L12 17.5l3.5 4z" fill="currentColor" /></>,
  finish: <><path d="M4 12l3.5 7L15 4h6" /><path d="M5.6 15.2h4" /><path d="M15.5 8h5" strokeWidth="1.2" /></>,
  weld: <><path d="M2.5 19l7-8" /><path d="M7.4 11.6L9.5 11l-.5 2" fill="currentColor" /><path d="M9.5 11h12" /><path d="M13 11v5l5-5z" fill="currentColor" /></>,
  balloon: <><circle cx="15" cy="9" r="6" /><path d="M14 7l1.5-1v6" strokeWidth="1.4" /><path d="M10.6 13.2L4.5 19.5" /><circle cx="4.2" cy="19.8" r="1.3" fill="currentColor" /></>,
  center: <><path d="M12 2.5v4M12 10v4M12 17.5v4M2.5 12h4M10 12h4M17.5 12h4" /></>,
  annotate: <><rect x="2.5" y="7" width="19" height="10" rx=".5" /><path d="M9 7v10" /><circle cx="5.75" cy="12" r="1.8" /><path d="M3.6 12h4.3M5.75 9.9v4.2" strokeWidth="1.2" /><path d="M11.5 12h7" strokeWidth="1.3" /></>,
  // pages and focus
  prev: <path d="M15 5l-7 7 7 7" />,
  next: <path d="M9 5l7 7-7 7" />,
  focus: <><path d="M4 8V5a1 1 0 011-1h3" /><path d="M16 4h3a1 1 0 011 1v3" /><path d="M20 16v3a1 1 0 01-1 1h-3" /><path d="M8 20H5a1 1 0 01-1-1v-3" /><rect x="8" y="8" width="8" height="8" rx="1" /></>,
  pdf: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5" /><path d="M8.5 16.5v-4h1.4a1.3 1.3 0 010 2.6H8.5M12.5 16.5v-4h1a2 2 0 010 4zM16.5 12.5h-1.6v4M14.9 14.4h1.3" strokeWidth="1.1" /></>,
  file: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5" /></>,
  upload: <><path d="M12 15V4" /><path d="M7 9l5-5 5 5" /><path d="M5 20h14" /></>,
  eraser: <><path d="M7 21h13" /><path d="M5.5 16.5l9-9.9a2 2 0 012.9 0l2.1 2.1a2 2 0 010 2.9L11 20H8.5z" /><path d="M9.5 11.8l5 5" /></>,
  shapes: <><rect x="3" y="11" width="9" height="9" rx="1" /><circle cx="16.5" cy="7.5" r="4.5" /></>,
  rect: <rect x="4" y="5" width="16" height="14" rx="1.5" />,
  ellipse: <ellipse cx="12" cy="12" rx="9" ry="7" />,
  diamond: <path d="M12 3l9 9-9 9-9-9z" />,
  triangle: <path d="M12 4l9 16H3z" />,
  line: <path d="M5 19L19 5" />,
  arrow: <><path d="M5 19L19 5" /><path d="M10 5h9v9" /></>,
  text: <><path d="M5 6V4h14v2" /><path d="M12 4v16" /><path d="M9 20h6" /></>,
  note: <><path d="M5 4h14v10l-6 6H5z" /><path d="M13 20v-6h6" /></>,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M21 16l-5-5L5 20" /></>,
  laser: <><path d="M5 3l14 8-6 1.6L10 19z" /><path d="M16.5 17.5l3 3" strokeWidth="2.4" /><circle cx="19.5" cy="20.5" r=".5" /></>,
  undo: <><path d="M9 14L4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 010 11H11" /></>,
  redo: <><path d="M15 14l5-5-5-5" /><path d="M20 9H9.5a5.5 5.5 0 000 11H13" /></>,
  download: <><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></>,
  expand: <><path d="M4 9V4h5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /><path d="M20 15v5h-5" /></>,
  collapse: <><path d="M9 4v5H4" /><path d="M15 4v5h5" /><path d="M9 20v-5H4" /><path d="M15 20v-5h5" /></>,
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus: <path d="M5 12h14" />,
  fit: <><path d="M4 8V4h4" /><path d="M20 8V4h-4" /><path d="M4 16v4h4" /><path d="M20 16v4h-4" /><rect x="8" y="8" width="8" height="8" rx="1" /></>,
  trash: <><path d="M4 7h16" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M6 7l1 13h10l1-13" /><path d="M9 7V4h6v3" /></>,
  duplicate: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>,
  unlock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 017.5-2" /></>,
  group: <><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /><path d="M3 15v6h6" strokeDasharray="2 2" /><path d="M21 9V3h-6" strokeDasharray="2 2" /></>,
  ungroup: <><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></>,
  front: <><rect x="8" y="8" width="12" height="12" rx="1" fill="currentColor" fillOpacity=".25" /><path d="M4 16V4h12" /></>,
  back: <><rect x="4" y="4" width="12" height="12" rx="1" fill="currentColor" fillOpacity=".25" /><path d="M20 8v12H8" /></>,
  grid: <><circle cx="6" cy="6" r="1" /><circle cx="12" cy="6" r="1" /><circle cx="18" cy="6" r="1" /><circle cx="6" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="18" cy="12" r="1" /><circle cx="6" cy="18" r="1" /><circle cx="12" cy="18" r="1" /><circle cx="18" cy="18" r="1" /></>,
  bold: <path d="M7 4h6a4 4 0 010 8H7zm0 8h7a4 4 0 010 8H7z" />,
  italic: <><path d="M10 4h8" /><path d="M6 20h8" /><path d="M15 4L9 20" /></>,
  alignLeft: <><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h13" /></>,
  alignCenter: <><path d="M4 6h16" /><path d="M7 12h10" /><path d="M5.5 18h13" /></>,
  alignRight: <><path d="M4 6h16" /><path d="M10 12h10" /><path d="M7 18h13" /></>,
  close: <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></>,
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  chevron: <path d="M7 10l5 5 5-5" />,
  pin: <><path d="M9 4h6l-1 6 3 3H7l3-3z" /><path d="M12 13v8" /></>,
  dashSolid: <path d="M3 12h18" />,
  dashDashed: <path d="M3 12h4m3 0h4m3 0h4" />,
  dashDotted: <path d="M3.5 12h.01m4 0h.01m4 0h.01m4 0h.01m4 0h.01" strokeWidth="2.6" />,
  headNone: <path d="M3 12h18" />,
  headArrow: <><path d="M3 12h18" /><path d="M15 7l6 5-6 5" /></>,
  headTriangle: <><path d="M3 12h13" /><path d="M14 7l7 5-7 5z" fill="currentColor" /></>,
  headDot: <><path d="M3 12h14" /><circle cx="18" cy="12" r="3" fill="currentColor" /></>,
  headBar: <><path d="M3 12h17" /><path d="M20 6v12" /></>,
  users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0" /><path d="M16 4.6a3.5 3.5 0 010 6.8" /><path d="M18 14.5a6.5 6.5 0 013.5 5.5" /></>,
  cloud: <path d="M7 18a4.5 4.5 0 01-.6-9A6 6 0 0118 9.5a4 4 0 01-.5 8.5z" />,
  cloudOff: <><path d="M7 18a4.5 4.5 0 01-.6-9 6 6 0 01.6-1" /><path d="M10.4 5.3A6 6 0 0118 9.5a4 4 0 011.8 7.3" /><path d="M3 3l18 18" /></>,
  more: <><circle cx="5" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="19" cy="12" r="1.2" /></>,
  grip: <><circle cx="9" cy="6" r="1.1" /><circle cx="15" cy="6" r="1.1" /><circle cx="9" cy="12" r="1.1" /><circle cx="15" cy="12" r="1.1" /><circle cx="9" cy="18" r="1.1" /><circle cx="15" cy="18" r="1.1" /></>,
};

export function Icon({ name, size = 18, className, style }) {
  return (
    <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {P[name] || null}
    </svg>
  );
}

// Where an element sits inside the layer.
function rectIn(layer, el) {
  const L = layer.getBoundingClientRect(); const r = el.getBoundingClientRect();
  return { left: r.left - L.left, top: r.top - L.top, width: r.width, height: r.height, layerW: L.width, layerH: L.height };
}

// A hover card: the name of a control and its keyboard shortcut.
export function Tip({ label, keys, side = 'bottom', children, disabled, className = '' }) {
  const layer = useContext(LayerContext);
  const ref = useRef(null);
  const timer = useRef(null);
  const [at, setAt] = useState(null);
  const show = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (ref.current && layer?.current) setAt(rectIn(layer.current, ref.current)); }, 350);
  }, [layer]);
  const hide = useCallback(() => { clearTimeout(timer.current); setAt(null); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  let card = null;
  if (at && !disabled && layer?.current) {
    const style = {};
    const cx = at.left + at.width / 2;
    if (side === 'right') { style.left = at.left + at.width + 8; style.top = at.top + at.height / 2; style.transform = 'translateY(-50%)'; }
    else if (side === 'top') { style.left = cx; style.top = at.top - 8; style.transform = 'translate(-50%, -100%)'; }
    else { style.left = cx; style.top = at.top + at.height + 8; style.transform = 'translateX(-50%)'; }
    if (side !== 'right') style.left = Math.max(70, Math.min(at.layerW - 70, style.left));
    card = createPortal(
      <div className="wb-tip" style={style} role="tooltip">
        <span>{label}</span>
        {keys && <span className="wb-tip-keys">{keys.split(' ').map((k) => <kbd key={k}>{k}</kbd>)}</span>}
      </div>, layer.current);
  }
  return (
    <span className={`wb-tipwrap ${className}`} ref={ref} onMouseEnter={show} onMouseLeave={hide} onPointerDown={hide}>
      {children}
      {card}
    </span>
  );
}

// A pop-over menu under (or beside) an anchor; closes on a click outside or Esc.
export function Popover({ anchor, open, onClose, side = 'bottom', align = 'center', children, className = '' }) {
  const layer = useContext(LayerContext);
  const box = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchor?.current || !layer?.current) { setPos(null); return; }
    const a = rectIn(layer.current, anchor.current);
    const s = {};
    if (side === 'right') { s.left = a.left + a.width + 8; s.top = a.top; } else if (side === 'top') { s.left = a.left; s.bottom = a.layerH - a.top + 8; } else { s.left = a.left; s.top = a.top + a.height + 8; }
    if (side !== 'right' && align === 'center') { s.left = a.left + a.width / 2; s.transform = 'translateX(-50%)'; }
    if (side !== 'right' && align === 'end') { s.left = undefined; s.right = a.layerW - (a.left + a.width); }
    setPos(s);
  }, [open, anchor, layer, side, align]);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => {
      if (box.current?.contains(e.target) || anchor?.current?.contains(e.target)) return;
      onClose?.();
    };
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('pointerdown', down, true); document.removeEventListener('keydown', key, true); };
  }, [open, onClose, anchor]);
  if (!open || !pos || !layer?.current) return null;
  return createPortal(<div ref={box} className={`wb-pop ${className}`} style={pos} onPointerDown={(e) => e.stopPropagation()}>{children}</div>, layer.current);
}

// A square tool / action button.
export function IconButton({ icon, label, keys, active, disabled, onClick, side, className = '', children, buttonRef, size = 18, ...rest }) {
  return (
    <Tip label={label} keys={keys} side={side} disabled={disabled}>
      <button ref={buttonRef} type="button" className={`wb-btn ${active ? 'is-active' : ''} ${className}`} aria-label={label}
        aria-pressed={active === undefined ? undefined : !!active} disabled={disabled} onClick={onClick} {...rest}>
        <Icon name={icon} size={size} />
        {children}
      </button>
    </Tip>
  );
}

export function Swatch({ color, active, onClick, label }) {
  const clear = !color || color === 'transparent';
  return (
    <Tip label={label}>
      <button type="button" className={`wb-swatch ${active ? 'is-active' : ''} ${clear ? 'is-clear' : ''}`} aria-label={label}
        aria-pressed={!!active} onClick={onClick} style={clear ? undefined : { '--sw': color }} />
    </Tip>
  );
}

// Initials on the person's colour.
export function Avatar({ name, color, size = 26, ring }) {
  const initials = String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
  return (
    <span className={`wb-avatar ${ring ? 'has-ring' : ''}`} style={{ '--av': color || '#64748b', width: size, height: size, fontSize: size * 0.4 }}>
      {initials}
    </span>
  );
}
